import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge } from '../../../components/ui';
import {
  DollarSign, Trophy, Percent, Banknote,
  ChevronDown, ChevronRight, AlertCircle, RefreshCw,
  Lock, Info,
} from 'lucide-react';
import { fechaLocalYMD, formatTimeCR } from '../../../utils/date';
import {
  fetchReportePorSorteo,
  fetchSorteosDelDia,
  type ReporteResponse,
  type ReporteSorteo,
  type SorteoDelDia,
} from '../../../services/reportesService';

const REFRESH_INTERVAL = 30_000;

function formatMoney(val: string | number | null | undefined): string {
  if (val === null || val === undefined) return '\u20A10';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '\u20A10';
  return `\u20A1${n.toLocaleString('es-CR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const TIPO_LABELS: Record<string, string> = {
  NORMAL: 'Normal',
  REVENTADO: 'Reventado',
  MEGA: 'Mega',
  PARLEY: 'Parley',
  FECHAS: 'Fechas',
  MONAZOS: 'Monazos',
};

const ESTADO_BADGE: Record<string, { variant: 'success' | 'warning' | 'neutral' | 'danger'; label: string }> = {
  ABIERTO: { variant: 'success', label: 'Abierto' },
  CERRADO: { variant: 'danger', label: 'Cerrado' },
  INACTIVO: { variant: 'neutral', label: 'Inactivo' },
};

// ─── Componente: Detalle de un sorteo ────────────────────────────

const SorteoCard: React.FC<{ reporte: ReporteSorteo }> = ({ reporte }) => {
  const [expanded, setExpanded] = useState(false);
  const estadoInfo = ESTADO_BADGE[reporte.estado] || { variant: 'neutral' as const, label: reporte.estado };
  const comisionNoGanada = parseFloat(reporte.comision_no_ganada);

  return (
    <Card className="overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{reporte.sorteo_nombre}</span>
              <Badge variant={estadoInfo.variant} size="sm">{estadoInfo.label}</Badge>
              <Badge variant="neutral" size="sm">{TIPO_LABELS[reporte.tipo_id] || reporte.tipo_id}</Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6 text-sm">
          <div className="text-right">
            <p className="text-gray-500">Vendido</p>
            <p className="font-bold text-gray-900">{formatMoney(reporte.total_vendido)}</p>
          </div>
          <div className="text-right">
            <p className="text-gray-500">Premios</p>
            <p className="font-bold text-warning-600">{formatMoney(reporte.total_premios)}</p>
          </div>
          <div className="text-right">
            <p className="text-gray-500">Comision</p>
            <p className="font-bold text-success-600">{formatMoney(reporte.comision_ganada)}</p>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-4 bg-gray-50/50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg p-3 border border-gray-100">
              <p className="text-xs text-gray-500">Vendido total</p>
              <p className="text-lg font-bold">{formatMoney(reporte.total_vendido)}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100">
              <p className="text-xs text-gray-500">Premios pagados</p>
              <p className="text-lg font-bold text-warning-600">{formatMoney(reporte.total_premios)}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100">
              <p className="text-xs text-gray-500">Comision ganada</p>
              <p className="text-lg font-bold text-success-600">{formatMoney(reporte.comision_ganada)}</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-100">
              <p className="text-xs text-gray-500">Resultado informativo</p>
              <p className="text-lg font-bold text-primary-600">{formatMoney(reporte.resultado_informativo)}</p>
            </div>
          </div>

          {/* Comisiones */}
          <div className="bg-white rounded-lg p-4 border border-gray-100 space-y-2">
            <h4 className="font-medium text-gray-700 text-sm flex items-center gap-1">
              <Percent size={14} /> Detalle de comisiones
            </h4>
            {reporte.comision_porcentaje && (
              <p className="text-sm text-gray-600">
                Porcentaje configurado: <span className="font-semibold">{reporte.comision_porcentaje}%</span>
              </p>
            )}
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Potencial</p>
                <p className="font-semibold">{formatMoney(reporte.comision_potencial)}</p>
              </div>
              <div>
                <p className="text-gray-500">Ganada</p>
                <p className="font-semibold text-success-600">{formatMoney(reporte.comision_ganada)}</p>
              </div>
              <div>
                <p className="text-gray-500">No ganada</p>
                <p className={`font-semibold ${comisionNoGanada > 0 ? 'text-danger-600' : 'text-gray-400'}`}>
                  {formatMoney(reporte.comision_no_ganada)}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 mt-2 p-2 rounded bg-gray-50 text-sm">
              <Info size={14} className="text-gray-400 mt-0.5 shrink-0" />
              <span className="text-gray-600">{reporte.comision_no_ganada_motivo}</span>
            </div>
          </div>

          {/* Restringidos (solo NORMAL/REVENTADO/MEGA) */}
          {reporte.usa_restringidos && (
            <div className="bg-white rounded-lg p-4 border border-gray-100 space-y-3">
              <h4 className="font-medium text-gray-700 text-sm flex items-center gap-1">
                <Lock size={14} /> Numeros restringidos vendidos
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Vendido en restringidos</p>
                  <p className="font-semibold text-danger-600">{formatMoney(reporte.total_vendido_restringidos)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Vendido en NO restringidos</p>
                  <p className="font-semibold text-success-600">{formatMoney(reporte.total_vendido_no_restringidos)}</p>
                </div>
              </div>
              {reporte.detalle_restringidos && reporte.detalle_restringidos.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-medium text-gray-500">Numero</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-500">Monto vendido</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-500">Comision perdida</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {reporte.detalle_restringidos.map((d) => (
                        <tr key={d.numero} className="hover:bg-gray-50">
                          <td className="py-2 px-3">
                            <span className="font-mono font-bold text-lg text-primary-600 bg-primary-50 px-2 py-0.5 rounded">
                              {d.numero}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-right font-medium">{formatMoney(d.monto_vendido)}</td>
                          <td className="py-2 px-3 text-right font-medium text-danger-600">{formatMoney(d.comision_perdida)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No se vendieron numeros restringidos en este sorteo</p>
              )}
            </div>
          )}

          {!reporte.usa_restringidos && (
            <div className="flex items-center gap-2 text-sm text-gray-400 italic p-2">
              <Info size={14} />
              <span>Este tipo de sorteo ({TIPO_LABELS[reporte.tipo_id] || reporte.tipo_id}) no usa numeros restringidos</span>
            </div>
          )}

          {/* Números ganadores del sorteo */}
          {reporte.ganadores && reporte.ganadores.length > 0 && (
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 space-y-2">
              <h4 className="font-medium text-yellow-700 text-sm flex items-center gap-1">
                <Trophy size={14} /> Números ganadores
              </h4>
              <div className="flex flex-wrap gap-2">
                {reporte.ganadores.map((g, i) => (
                  <span key={i} className="font-mono font-bold text-lg bg-yellow-100 text-yellow-800 px-3 py-1 rounded-lg">
                    {[g.num1, g.num2, g.num3].filter(Boolean).join('-')}
                    {g.tipo_monazos && (
                      <span className="text-xs ml-1 text-yellow-600">({g.tipo_monazos})</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Premios por combinación */}
          {reporte.premios_por_combo && reporte.premios_por_combo.length > 0 && (
            <div className="bg-white rounded-lg p-4 border border-yellow-200 space-y-2">
              <h4 className="font-medium text-yellow-700 text-sm flex items-center gap-1">
                <Trophy size={14} /> Premios por combinación
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-yellow-100">
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Combinación</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-500">Monto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reporte.premios_por_combo.map((p) => (
                      <tr key={p.combinacion} className="hover:bg-yellow-50">
                        <td className="py-2 px-3 font-mono font-bold text-yellow-700">{p.combinacion}</td>
                        <td className="py-2 px-3 text-right font-bold text-yellow-600">{formatMoney(p.monto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Tickets premiados pagados */}
          {reporte.tickets_premiados && reporte.tickets_premiados.length > 0 && (
            <div className="bg-white rounded-lg p-4 border border-green-200 space-y-2">
              <h4 className="font-medium text-green-700 text-sm flex items-center gap-2">
                <Trophy size={14} />
                <span>Tickets premiados ({reporte.tickets_premiados.length})</span>
                {reporte.total_premios_pagados && (
                  <span className="ml-auto text-xs font-normal text-gray-500">
                    Total pagado:{' '}
                    <span className="font-bold text-green-700">
                      {formatMoney(reporte.total_premios_pagados)}
                    </span>
                  </span>
                )}
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-green-100">
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Ticket</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Vendedor</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-500">Apostado</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-500">Premio</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reporte.tickets_premiados.map((tp, i) => (
                      <tr key={i} className="hover:bg-green-50">
                        <td className="py-2 px-3 font-mono text-xs font-bold text-gray-700">{tp.ticket_number}</td>
                        <td className="py-2 px-3 text-gray-600">{tp.vendedor_id || '—'}</td>
                        <td className="py-2 px-3 text-right font-medium">{formatMoney(tp.monto_apostado)}</td>
                        <td className="py-2 px-3 text-right font-bold text-green-600">{formatMoney(tp.premio_pagado)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// ─── Pagina principal ────────────────────────────────────────────

export const LocalReportesPage: React.FC = () => {
  const [fecha, setFecha] = useState(fechaLocalYMD());
  const [sorteoId, setSorteoId] = useState('');
  const [reporte, setReporte] = useState<ReporteResponse | null>(null);
  const [sorteos, setSorteos] = useState<SorteoDelDia[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  useEffect(() => {
    fetchSorteosDelDia(fecha)
      .then(setSorteos)
      .catch(() => {});
  }, [fecha]);

  const cargarReporte = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // El backend aplica local_filter automaticamente segun el rol del usuario
      const data = await fetchReportePorSorteo(fecha, sorteoId || undefined);
      setReporte(data);
      setLastUpdate(new Date());
    } catch (err: any) {
      setError(err.message || 'Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  }, [fecha, sorteoId]);

  useEffect(() => {
    cargarReporte();
  }, [cargarReporte]);

  useEffect(() => {
    const interval = setInterval(cargarReporte, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [cargarReporte]);

  const totales = reporte?.totales;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reportes</h1>
          <p className="text-gray-500">Reporte informativo de ventas, premios y comisiones</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdate && (
            <span className="text-xs text-gray-400">
              Actualizado: {formatTimeCR(lastUpdate.toISOString())}
            </span>
          )}
          <button
            onClick={cargarReporte}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => { setFecha(e.target.value); setSorteoId(''); }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Sorteo</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={sorteoId}
              onChange={(e) => setSorteoId(e.target.value)}
            >
              <option value="">Todos los sorteos</option>
              {sorteos.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre} ({TIPO_LABELS[s.tipo_id] || s.tipo_id})</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger-50 text-danger-600 rounded-lg border border-danger-200">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Banner de totales */}
      {totales && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <div className="text-center">
              <DollarSign className="mx-auto text-primary-600 mb-2" size={24} />
              <p className="text-sm text-gray-500">Ventas</p>
              <p className="text-xl font-bold">{formatMoney(totales.total_vendido)}</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <Trophy className="mx-auto text-warning-600 mb-2" size={24} />
              <p className="text-sm text-gray-500">Premios</p>
              <p className="text-xl font-bold text-warning-600">{formatMoney(totales.total_premios)}</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <Percent className="mx-auto text-success-600 mb-2" size={24} />
              <p className="text-sm text-gray-500">Comisiones</p>
              <p className="text-xl font-bold text-success-600">{formatMoney(totales.total_comision_ganada)}</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <Banknote className="mx-auto text-primary-600 mb-2" size={24} />
              <p className="text-sm text-gray-500">Pagas</p>
              <p className="text-xl font-bold text-primary-600">{formatMoney(totales.total_pagas)}</p>
            </div>
          </Card>
        </div>
      )}

      {/* Lista de sorteos */}
      {reporte && reporte.sorteos.length > 0 ? (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-800">Detalle por sorteo</h2>
          {reporte.sorteos.map((s) => (
            <SorteoCard key={s.sorteo_id} reporte={s} />
          ))}
        </div>
      ) : reporte && !loading ? (
        <Card>
          <div className="text-center py-12 text-gray-400">
            <AlertCircle size={48} className="mx-auto mb-3" />
            <p className="text-lg font-medium">No hay sorteos para esta fecha</p>
            <p className="text-sm mt-1">Seleccione otra fecha o verifique que existan sorteos configurados</p>
          </div>
        </Card>
      ) : null}

      {loading && !reporte && (
        <Card>
          <div className="text-center py-12 text-gray-400">
            <RefreshCw size={32} className="mx-auto mb-3 animate-spin" />
            <p>Cargando reporte...</p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default LocalReportesPage;
